import { Editor } from 'svelte-tiptap';
import { Slice, Fragment, NodeType, ResolvedPos, Node as ProseMirrorNode, Schema } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { cloneDeep, uniq } from 'lodash-es';
import { getText, getTextSerializersFromSchema } from '@tiptap/core';
import { convertToPt } from './extension-word/utils';
import { adjustEndPosition, adjustStartPosition } from './extension/Highlight';

export type SlicedContent = { start: number; end: number; slice: any; content: Record<string, any>[]; openStart: number; openEnd: number };
export type GuidOffsetRange = { startGuid: string; startOffset: number; endGuid: string; endOffset: number };
export type GuidOffsetPosition = { guid: string; start: number; end: number };
export type GuidOffsetSlicedContent = SlicedContent & { hasText: boolean };
export interface EditorUtils {
    updateAllHighlighting(positions: { id: string; start: number; end: number }[]): void;
    updateAllHighlightingWithGuid(positions: { id?: string; guid: string; start: number; end: number }[], focusedClassName?: string): void;
    updateAllHighlightingWithGuidRange(position: { id?: string; startGuid: string; endGuid: string }): void;
    setHighlightingWithTextContent(text: string): Promise<{ id: string; from: number; to: number }>;
    getContentsJsonWithGuids(startGuid: string, endGuid: string): SlicedContent;
    getContentsJsonWithGuidOffsets(range: GuidOffsetRange): GuidOffsetSlicedContent;
    getTextLengthWithGuid(guid: string): number;
    getGuidOffsetPositions(range: GuidOffsetRange): GuidOffsetPosition[];
    replaceContentWithGuids(startGuid: string, endGuid: string, replacedContent: ReadonlyArray<object>): (SlicedContent & { replacedStartGuid: string; replacedEndGuid: string }) | undefined;
    replaceContentWithGuidOffsets(range: GuidOffsetRange, replacedContent: ReadonlyArray<object>, options?: { allowEmptySourceRange?: boolean }): GuidOffsetRange | undefined;
    replaceContentWithSelection(replacedContent: Record<string, any>[]): SlicedContent & { replacedStartGuid: string; replacedEndGuid: string };
    getMinLeftMargin(): number;
    /**
     * CQA-727: `afterGuid` 문단 바로 뒤에 새 블록들을 독립된 형제 노드로 삽입한다. `replaceContentWithGuidOffsets`의
     * zero-width 삽입(maxOpen)과 달리 앵커 문단의 텍스트에 병합하지 않는다 — 반환된 guid는 노드 경계 그대로라
     * `removeSiblingBlocksByGuids`로 인접 문단과의 병합 충돌 없이 되돌릴 수 있다.
     */
    insertSiblingBlocksAfterGuid(afterGuid: string, content: ReadonlyArray<object>): { guids: string[] } | undefined;
    /** CQA-727: `insertSiblingBlocksAfterGuid`가 반환한 guid(문서 순서, 연속된 형제)를 노드 경계로 통째로 지운다. */
    removeSiblingBlocksByGuids(guids: readonly string[]): boolean;
    /**
     * `WordNumberingList` 데코레이션이 렌더한 번호 라벨(예: "제13조")을 그 라벨이 붙은 문단의 guid와 짝지어 돌려준다.
     * 자동 번호 조항 제목의 '제N조'는 문서 JSON 텍스트가 아니라 `numberingPart` 기반 데코레이션에만 있다.
     */
    getListNumberLabels(): ReadonlyMap<string, string>;
}
declare module '@tiptap/core' {
    interface Editor {
        initializeUtils(): void;
        utils: EditorUtils;
    }
}

/**
 * ProseMirror JSON에서 guid를 교체하는 함수
 * @param json ProseMirror JSON
 * @param targetGuid 업데이트할 노드의 guid
 * @param newGuid 새로운 guid
 * @returns 업데이트된 ProseMirror JSON
 * @example
 * const updatedJSON = replaceGuidInProseMirrorJSON(json, targetGuid, newGuid);
 */
export function replaceGuidInProseMirrorJSON(data: any, targetGuid: string, newGuid: string) {
    // 노드의 guid 속성을 업데이트하는 재귀 함수
    function updateNode(node) {
        // attrs.guid가 targetGuid와 일치하면 새로운 guid로 교체
        if (node.attrs && node.attrs.guid === targetGuid) {
            node.attrs.guid = newGuid;
        }

        // 자식 노드가 있는 경우 재귀적으로 처리
        if (node.content && Array.isArray(node.content)) {
            node.content = node.content.map(updateNode);
        }

        return node;
    }

    // 데이터가 doc 타입인지 content 배열인지 확인
    if (Array.isArray(data)) {
        return data.map(updateNode);
    } else if (data.type === 'doc' && Array.isArray(data.content)) {
        data.content = data.content.map(updateNode);
        return data;
    } else {
        throw new Error('Invalid input format. Expected a doc or content array.');
    }
}

/**
 * ProseMirror JSON에서 guid를 갱신하는 함수
 * @param json ProseMirror JSON
 * @returns 갱신된 ProseMirror JSON
 * @example
 * const updatedJSON = renewGuidInProseMirrorJSON(json);
 */
export function renewGuidInProseMirrorJSON(data: any) {
    // 노드의 guid 속성을 업데이트하는 재귀 함수
    function updateNode(node) {
        if (node.attrs && node.attrs.guid) {
            node.attrs.guid = crypto.randomUUID();
        }

        // 자식 노드가 있는 경우 재귀적으로 처리
        if (node.content && Array.isArray(node.content)) {
            node.content = node.content.map(updateNode);
        }

        return node;
    }

    // 데이터가 doc 타입인지 content 배열인지 확인
    if (Array.isArray(data)) {
        return data.map(updateNode);
    } else if (data.type === 'doc' && Array.isArray(data.content)) {
        data.content = data.content.map(updateNode);
        return data;
    } else {
        throw new Error('Invalid input format. Expected a doc or content array.');
    }
}

function collectGuidNodes(document: ProseMirrorNode): Map<string, Array<{ node: ProseMirrorNode; pos: number }>> {
    const nodes = new Map<string, Array<{ node: ProseMirrorNode; pos: number }>>();
    document.descendants((node, pos) => {
        const guid = node.attrs.guid;
        if (typeof guid !== 'string' || guid.length === 0) return;
        const entries = nodes.get(guid);
        if (entries === undefined) {
            nodes.set(guid, [{ node, pos }]);
            return;
        }
        entries.push({ node, pos });
    });
    return nodes;
}

function collectFragmentGuids(fragment: Fragment): string[] {
    const guids: string[] = [];
    fragment.descendants(node => {
        const guid = node.attrs.guid;
        if (typeof guid === 'string' && guid.length > 0) guids.push(guid);
    });
    return guids;
}

type ProtectedGuidNode = { guid: string; node: ProseMirrorNode; pos: number; depth: number; edge: 'start' | 'end' };

function collectPartiallyCoveredGuidNodes(document: ProseMirrorNode, start: number, end: number): ProtectedGuidNode[] {
    const protectedNodes: ProtectedGuidNode[] = [];
    document.descendants((node, pos) => {
        const guid = node.attrs.guid;
        if (typeof guid !== 'string' || guid.length === 0) return true;
        const nodeEnd = pos + node.nodeSize;
        if (pos >= end || nodeEnd <= start) return true;
        const includesStartBoundary = pos < start;
        const includesEndBoundary = end < nodeEnd;
        if (!includesStartBoundary && !includesEndBoundary) return true;
        protectedNodes.push({
            guid,
            node,
            pos,
            depth: document.resolve(pos + 1).depth,
            edge: includesStartBoundary ? 'start' : 'end',
        });
        return true;
    });
    return protectedNodes;
}

function createGuid(usedGuids: Set<string>): string {
    let guid = crypto.randomUUID();
    while (usedGuids.has(guid)) guid = crypto.randomUUID();
    usedGuids.add(guid);
    return guid;
}

function hasParagraphGuid(node: ProseMirrorNode): boolean {
    return node.type.name === 'paragraph' && typeof node.attrs.guid === 'string' && node.attrs.guid.length > 0;
}

/**
 * 삽입된 top-level 블록의 되돌리기 핸들 guid — paragraph/table처럼 자기 guid를 갖는 블록은 그 값이고,
 * numberingList는 guid attr이 없으므로(WordUniqueId 대상이 아니다) 유일한 자식 문단의 guid를 핸들로 쓴다.
 * 핸들을 정할 수 없는 블록은 삽입 자체를 거절해야 undo 경계가 끊기지 않는다(CQA-803).
 */
function insertedBlockHandleGuid(node: ProseMirrorNode): string | undefined {
    const guid = node.attrs.guid;
    if (typeof guid === 'string' && guid.length > 0) return guid;
    if (node.type.name === 'numberingList' && node.childCount === 1) {
        const childGuid = node.child(0).attrs.guid;
        if (typeof childGuid === 'string' && childGuid.length > 0) return childGuid;
    }
    return undefined;
}

function findParagraphGuidNode(document: ProseMirrorNode, guid: string): { node: ProseMirrorNode; pos: number } | undefined {
    let result: { node: ProseMirrorNode; pos: number } | undefined;
    document.descendants((node, pos) => {
        if (!hasParagraphGuid(node) || node.attrs.guid !== guid) return true;
        result = { node, pos };
        return false;
    });
    return result;
}

function normalizeReplacementNode(node: ProseMirrorNode, usedGuids: Set<string>): ProseMirrorNode {
    if (node.isText) return node;
    const children: ProseMirrorNode[] = [];
    node.forEach(child => children.push(normalizeReplacementNode(child, usedGuids)));
    const content = Fragment.from(children);
    const attrs = node.type.spec.attrs;
    if (attrs !== undefined && Object.hasOwn(attrs, 'guid') && (typeof node.attrs.guid !== 'string' || node.attrs.guid.length === 0)) {
        return node.type.create({ ...node.attrs, guid: createGuid(usedGuids) }, content, node.marks);
    }
    return node.copy(content);
}

function normalizeReplacementFragment(schema: Schema, content: ReadonlyArray<object>, usedGuids: Set<string>): Fragment {
    const replacement = Fragment.fromJSON(schema, content);
    const guids = collectFragmentGuids(replacement);
    if (uniq(guids).length !== guids.length) throw new Error('Replacement contains duplicate guid.');
    const nodes: ProseMirrorNode[] = [];
    replacement.forEach(node => nodes.push(normalizeReplacementNode(node, usedGuids)));
    return Fragment.from(nodes);
}

function normalizeSlicedContent(node: ProseMirrorNode, content: Record<string, any>[] | null): Record<string, any>[] {
    // Fragment.toJSON()은 빈 조각을 null로 반환한다 — 그대로 감싸면 `{content: null}`이라는 무효한 노드 모양이
    // 나온다(예: 삭제-only 승인 뒤 접힌 zero-width 범위를 다시 읽는 경우). 아무것도 추출되지 않았으면 빈 배열이다.
    if (content === null) return [];
    if (!node.isBlock) return content;
    return [{ ...node.toJSON(), content }];
}

function guidOffsetLength(node: ProseMirrorNode): number {
    let hardBreakCount = 0;
    node.descendants(child => {
        if (child.type.name === 'hardBreak') hardBreakCount += 1;
    });
    return node.textContent.length + hardBreakCount;
}

function offsetForPosition(node: ProseMirrorNode, nodePos: number, position: number, edge: 'start' | 'end'): number | undefined {
    for (let offset = 0; offset <= guidOffsetLength(node); offset += 1) {
        const relativePosition = edge === 'start' ? adjustStartPosition(node, offset) : adjustEndPosition(node, offset);
        if (nodePos + relativePosition + (node.isBlock ? 1 : 0) === position) return offset;
    }
    return undefined;
}

function guidOffsetForPosition(document: ProseMirrorNode, position: number, edge: 'start' | 'end'): { guid: string; offset: number } {
    let result: { guid: string; offset: number } | undefined;
    document.descendants((node, nodePos) => {
        if (result !== undefined) return false;
        if (!hasParagraphGuid(node)) return true;
        const offset = offsetForPosition(node, nodePos, position, edge);
        if (offset !== undefined) result = { guid: node.attrs.guid, offset };
        return result === undefined;
    });
    if (result === undefined) throw new Error(`Replacement ${edge} position has no guid paragraph.`);
    return result;
}

function findProtectedGuidNode(transaction: Transaction, protectedNode: ProtectedGuidNode): { node: ProseMirrorNode; pos: number } {
    const mappedPosition = protectedNode.edge === 'start' ? transaction.mapping.map(protectedNode.pos, 1) : transaction.mapping.map(protectedNode.pos + protectedNode.node.nodeSize, -1);
    const matches: Array<{ node: ProseMirrorNode; pos: number }> = [];
    transaction.doc.descendants((node, pos) => {
        const matchesBoundary = protectedNode.edge === 'start' ? pos === mappedPosition : pos + node.nodeSize === mappedPosition;
        if (node.type !== protectedNode.node.type || transaction.doc.resolve(pos + 1).depth !== protectedNode.depth || !matchesBoundary) return true;
        matches.push({ node, pos });
        return true;
    });
    if (matches.length !== 1) throw new Error(`Replacement would lose protected guid: ${protectedNode.guid}.`);
    return matches[0];
}

function setProtectedGuidNodes(transaction: Transaction, protectedNodes: ReadonlyArray<ProtectedGuidNode>): Set<number> {
    const resolvedNodes = protectedNodes.map(protectedNode => ({ ...protectedNode, resolvedNode: findProtectedGuidNode(transaction, protectedNode) }));
    const protectedNodePositions = new Map<number, { node: ProseMirrorNode; guid: string }>();
    resolvedNodes.forEach(({ resolvedNode, guid }) => {
        const protectedGuid = protectedNodePositions.get(resolvedNode.pos)?.guid;
        if (protectedGuid !== undefined && protectedGuid !== guid) throw new Error(`Replacement boundary guids collide: ${protectedGuid} and ${guid}.`);
        protectedNodePositions.set(resolvedNode.pos, { node: resolvedNode.node, guid });
    });
    protectedNodePositions.forEach(({ node, guid }, pos) => transaction.setNodeMarkup(pos, undefined, { ...node.attrs, guid }));
    return new Set(protectedNodePositions.keys());
}

function repairSplitGuidDuplicates(transaction: Transaction, sourceGuids: ReadonlySet<string>, protectedNodePositions: ReadonlySet<number>): void {
    const usedGuids = new Set(collectGuidNodes(transaction.doc).keys());
    collectGuidNodes(transaction.doc).forEach((nodes, guid) => {
        if (nodes.length < 2) return;
        if (!sourceGuids.has(guid)) throw new Error('Replacement would create duplicate guid.');
        const protectedNodes = nodes.filter(({ pos }) => protectedNodePositions.has(pos));
        if (protectedNodes.length > 1) throw new Error('Replacement would duplicate a protected guid.');
        nodes.filter(({ pos }) => !protectedNodePositions.has(pos)).forEach(({ node, pos }) => transaction.setNodeMarkup(pos, undefined, { ...node.attrs, guid: createGuid(usedGuids) }));
    });
    collectGuidNodes(transaction.doc).forEach(nodes => {
        if (nodes.length > 1) throw new Error('Replacement would create duplicate guid.');
    });
}

export function setPrototypeOfEditorUtils() {
    // Editor extension의 Return은 ReturnType만을 받다보니 실제 데이터 리턴을 처리하기 위해 별도의 prototype으로 처리
    Editor.prototype.initializeUtils = function () {
        this.utils = {
            updateAllHighlighting: (positions: { id: string; start: number; end: number }[]) => {
                this.commands.removeAllHighlighting();
                positions.forEach(({ id, start, end }) => {
                    this.commands.addHighlightingWithTextPosition(id, start, end, {
                        className: 'bg-none',
                        focusedClassName: 'bg-purple-200 text-gray-900',
                    });
                });
                this.commands.setFocusHighlighting(positions[0]?.id);
                setTimeout(() => {
                    this.commands.scrollIntoFocusedHighlighting();
                }, 0);
            },
            // positions 안의 id 지정은 전부 또는 전무여야 한다 — 일부만 지정하면 focus는 positions[0].id만 겨냥한다.
            updateAllHighlightingWithGuid: (positions: { id?: string; guid: string; start: number; end: number }[], focusedClassName?: string) => {
                this.commands.removeAllHighlighting();
                // 문단 여러 개가 한 번의 호출(하나의 조항)에 속한다 — 문단마다 새 id를 뽑으면 focus는 첫 문단에만
                // 걸려 나머지 문단은 focusedClassName이 적용되지 않는다(bg-none으로 안 보임). 같은 호출의 모든
                // 문단이 하나의 하이라이트로 함께 보이도록 id를 공유한다.
                const sharedId = crypto.randomUUID();
                positions.forEach(({ id, guid, start, end }) => {
                    this.commands.addHighlightingWithGuid(id || sharedId, guid, {
                        startInlineOffset: start,
                        endInlineOffset: end,
                        className: 'bg-none',
                        focusedClassName: focusedClassName || 'bg-purple-200 text-gray-900',
                    });
                });
                this.commands.setFocusHighlighting(positions[0]?.id || sharedId);
                setTimeout(() => {
                    this.commands.scrollIntoFocusedHighlighting();
                }, 0);
            },
            updateAllHighlightingWithGuidRange: ({ id, startGuid, endGuid }) => {
                this.commands.removeAllHighlighting();
                const newId = id || crypto.randomUUID();
                this.commands.addHighlightingWithGuidRange(newId, startGuid, endGuid, {
                    className: 'bg-none',
                    focusedClassName: 'bg-purple-200 text-gray-900',
                });
                this.commands.setFocusHighlighting(newId);
                setTimeout(() => {
                    this.commands.scrollIntoFocusedHighlighting();
                }, 0);
            },
            // deprecated
            setHighlightingWithTextContent: async text => {
                this.commands.removeAllHighlighting();
                const promise = new Promise((resolve, reject) => {
                    this.commands.addHighlightingWithTextContent(text, {
                        className: 'bg-none',
                        focusedClassName: 'bg-purple-200 text-gray-900',
                        onAddFinish: ({ id, from, to }) => {
                            resolve({ id, from, to });
                        },
                    });
                });
                const { id, from, to } = (await promise) as { id: string; from: number; to: number };
                if (id) {
                    this.commands.setFocusHighlighting(id as string);
                    setTimeout(() => {
                        this.commands.scrollIntoFocusedHighlighting();
                    }, 0);
                }
                return { id, from, to };
            },
            /**
             * @description startGuid와 endGuid 사이의 JSON 데이터를 가져온다.
             * @param startGuid 시작지점이 되는 guid
             * @param endGuid 끝지점이 되는 guid
             * @returns JSON 리턴값은 slice 객체를 리턴한다. 이 객체는 openStart와 openEnd 를 포함하고 있고 최상위를 fragment로 감싸서 리턴한다.
             */
            getContentsJsonWithGuids: (startGuid, endGuid) => {
                let startNode;
                let startNodePos;
                let endNode;
                let endNodePos;
                this.state.doc.descendants((node, pos) => {
                    if (node.attrs.guid === startGuid) {
                        startNode = node;
                        startNodePos = pos;
                    }
                    if (node.attrs.guid === endGuid) {
                        endNode = node;
                        endNodePos = pos;
                    }
                });
                if (!startNode || !endNode) {
                    console.warn('guid에 해당하는 node를 발견하지 못했습니다.', startGuid, endGuid, startNode, endNode);
                    return {
                        start: 0,
                        end: 0,
                        slice: null,
                        content: [],
                        openStart: 0,
                        openEnd: 0,
                    };
                }
                const slice = this.state.doc.slice(startNodePos, endNodePos + endNode.nodeSize, false);
                return {
                    start: startNodePos,
                    end: endNodePos + endNode.nodeSize,
                    slice,
                    content: slice.content.toJSON(),
                    openStart: slice.openStart,
                    openEnd: slice.openEnd,
                };
            },
            getTextLengthWithGuid: guid => {
                const nodeEntry = findParagraphGuidNode(this.state.doc, guid);
                if (nodeEntry === undefined) throw new Error(`Editor paragraph ${guid} is not initialized.`);
                return guidOffsetLength(nodeEntry.node);
            },
            getContentsJsonWithGuidOffsets: ({ startGuid, startOffset, endGuid, endOffset }) => {
                const startNodeEntry = findParagraphGuidNode(this.state.doc, startGuid);
                const endNodeEntry = findParagraphGuidNode(this.state.doc, endGuid);
                const startNode = startNodeEntry?.node;
                const startNodePos = startNodeEntry?.pos;
                const endNode = endNodeEntry?.node;
                const endNodePos = endNodeEntry?.pos;
                if (
                    startNode === undefined ||
                    startNodePos === undefined ||
                    endNode === undefined ||
                    endNodePos === undefined ||
                    !Number.isInteger(startOffset) ||
                    !Number.isInteger(endOffset) ||
                    startOffset < 0 ||
                    endOffset < 0 ||
                    startOffset > guidOffsetLength(startNode) ||
                    endOffset > guidOffsetLength(endNode)
                ) {
                    return { start: 0, end: 0, slice: null, content: [], openStart: 0, openEnd: 0, hasText: false };
                }
                const start = startNodePos + adjustStartPosition(startNode, startOffset) + (startNode.isBlock ? 1 : 0);
                const end = Math.min(endNodePos + adjustEndPosition(endNode, endOffset) + (endNode.isBlock ? 1 : 0), endNodePos + endNode.nodeSize);
                if (start > end) return { start: 0, end: 0, slice: null, content: [], openStart: 0, openEnd: 0, hasText: false };
                const slice = this.state.doc.slice(start, end, false);
                const content = slice.content.toJSON();
                return {
                    start,
                    end,
                    slice,
                    content: startNode === endNode ? normalizeSlicedContent(startNode, content) : content,
                    openStart: slice.openStart,
                    openEnd: slice.openEnd,
                    hasText: slice.content.textBetween(0, slice.content.size, '', '') !== '',
                };
            },
            getGuidOffsetPositions: range => {
                const { start, end, slice } = this.utils.getContentsJsonWithGuidOffsets(range);
                if (!slice || start >= end || slice.content.size === 0 || slice.content.textBetween(0, slice.content.size, '', '') === '') return [];
                const positions: GuidOffsetPosition[] = [];
                this.state.doc.descendants((node, nodePos) => {
                    if (!hasParagraphGuid(node)) return;
                    const nodeStart = nodePos + 1;
                    const nodeEnd = nodePos + node.nodeSize - 1;
                    const rangeStart = Math.max(start, nodeStart);
                    const rangeEnd = Math.min(end, nodeEnd);
                    if (rangeStart >= rangeEnd) return;
                    const startOffset = offsetForPosition(node, nodePos, rangeStart, 'start');
                    const endOffset = offsetForPosition(node, nodePos, rangeEnd, 'end');
                    if (startOffset === undefined || endOffset === undefined) throw new Error(`Guid ${node.attrs.guid} cannot represent the replacement range.`);
                    positions.push({ guid: node.attrs.guid, start: startOffset, end: endOffset });
                });
                if (positions.length === 0) throw new Error('Replacement range has no guid positions.');
                return positions;
            },
            /**
             * @description startGuid와 endGuid 사이의 JSON 데이터를 replacedContent로 교체한다.
             * @param startGuid 시작지점이 되는 guid
             * @param endGuid 끝지점이 되는 guid
             * @param replacedContent 교체할 JSON 배열 데이터
             * @returns 교체된 JSON 데이터를 리턴한다.
             */
            replaceContentWithGuids: (startGuid, endGuid, replacedContent) => {
                const { start, end, slice, content, openStart, openEnd } = this.utils.getContentsJsonWithGuids(startGuid, endGuid);
                if (!slice) {
                    console.warn('guid에 해당하는 범위를 찾지 못했습니다.', startGuid, endGuid);
                    return;
                }
                // 치환되어질 guid들과 치환할 guid 목록을 만들어서 같지 않으면 새로운 guid를 부여한다.
                const originGuids: string[] = [];
                const replacedGuids: string[] = [];
                slice.content.descendants((node, pos) => {
                    if (node.attrs.guid) {
                        originGuids.push(node.attrs.guid);
                    }
                });
                let fragment = Fragment.fromJSON(this.state.schema, replacedContent);
                const willChangeGuids: string[] = [];
                fragment.descendants((node, pos) => {
                    if (node.attrs.guid && !originGuids.includes(node.attrs.guid)) {
                        willChangeGuids.push(node.attrs.guid);
                    }
                });
                if (willChangeGuids.length) {
                    let nextContent = cloneDeep(replacedContent);
                    willChangeGuids.forEach(guid => {
                        nextContent = replaceGuidInProseMirrorJSON(nextContent, guid, crypto.randomUUID());
                    });
                    fragment = Fragment.fromJSON(this.state.schema, nextContent);
                }
                fragment.descendants(node => {
                    if (typeof node.attrs.guid === 'string' && node.attrs.guid.length > 0) {
                        replacedGuids.push(node.attrs.guid);
                    }
                });
                if (uniq(replacedGuids).length !== replacedGuids.length) {
                    console.warn('교체된 데이터에 중복된 guid가 존재합니다.', replacedGuids);
                }
                if (replacedGuids.length === 0) {
                    console.warn('교체된 데이터에 guid 경계가 없습니다.', startGuid, endGuid);
                    return;
                }
                const newSlice = new Slice(fragment, 0, 0);
                const newTr = this.view.state.tr.replace(start - openStart, end + openEnd, newSlice);
                this.view.dispatch(newTr);

                return {
                    ...this.utils.getContentsJsonWithGuids(replacedGuids[0], replacedGuids[replacedGuids.length - 1]),
                    replacedStartGuid: replacedGuids[0],
                    replacedEndGuid: replacedGuids[replacedGuids.length - 1],
                };
            },
            replaceContentWithGuidOffsets: (range, replacedContent, options) => {
                const allowEmptySourceRange = options?.allowEmptySourceRange === true;
                const { start, end, slice } = this.utils.getContentsJsonWithGuidOffsets(range);
                // allowEmptySourceRange: 변경추적 OFF에서 삭제-only 수정안을 승인해 범위 안 내용이 이미 전부 사라진
                // 뒤, 그 지점에 원본을 되돌리는 Undo 전용 경로다 — 호출부(undoAppliedRevision)가 직전에 저장된
                // appliedElements가 실제로 비어 있음을 이미 확인한 뒤에만 넘긴다. 그 외에는 항상 실제 소스 범위를 요구한다.
                if (!slice) return;
                if (!allowEmptySourceRange && (start >= end || slice.content.size === 0 || slice.content.textBetween(0, slice.content.size, '', '') === '')) return;
                if (allowEmptySourceRange && start !== end) return;
                const documentGuidNodes = collectGuidNodes(this.state.doc);
                documentGuidNodes.forEach(nodes => {
                    if (nodes.length > 1) throw new Error('Document contains duplicate guid.');
                });
                const protectedNodes = collectPartiallyCoveredGuidNodes(this.state.doc, start, end);
                const sourceGuids = new Set([range.startGuid, range.endGuid]);
                slice.content.descendants(node => {
                    if (typeof node.attrs.guid === 'string' && node.attrs.guid.length > 0) sourceGuids.add(node.attrs.guid);
                });
                protectedNodes.forEach(({ guid }) => sourceGuids.add(guid));
                const rawReplacement = Fragment.fromJSON(this.state.schema, replacedContent);
                const rawGuids = collectFragmentGuids(rawReplacement);
                if (uniq(rawGuids).length !== rawGuids.length) throw new Error('Replacement contains duplicate guid.');
                rawGuids.forEach(guid => {
                    if (documentGuidNodes.has(guid) && !sourceGuids.has(guid)) throw new Error('Replacement guid collides with the document.');
                });
                const fragment = normalizeReplacementFragment(this.state.schema, replacedContent, new Set(documentGuidNodes.keys()));
                // 빈 배열(교체 결과가 아무 콘텐츠도 남기지 않음, 예: 변경추적 OFF에서 삭제-only 수정안을 승인)은
                // 유효한 "이 범위를 통째로 삭제" 요청이다 — placeholder로 채우지 않고 그대로 지운다. 반면 구조는
                // 있으나 텍스트만 없는 경우(예: 빈 문단 하나)는 여전히 모호한 입력으로 거부한다(기존 계약 유지).
                const isDeletion = replacedContent.length === 0;
                if (!isDeletion && (fragment.size === 0 || fragment.textBetween(0, fragment.size, '', '') === '')) return;
                // 순수 삽입(zero-width 소스 범위에 실제 콘텐츠를 넣는 것, 예: 삭제-only 승인 뒤 Undo로 원본을 되돌리는
                // 경로)은 삽입 지점 그 자체를 both start/end로 매핑하면 안 된다 — 일반 교체와 반대 방향(assoc)으로
                // 매핑해야 새로 들어간 콘텐츠의 시작과 끝을 올바르게 감싼다(그렇지 않으면 뒤집힌 범위가 나온다).
                const isPureInsertion = allowEmptySourceRange && !isDeletion;
                const newTr = this.view.state.tr.replace(start, end, Slice.maxOpen(fragment, true));
                const replacementStart = newTr.mapping.map(start, isPureInsertion ? -1 : 1);
                const replacementEnd = newTr.mapping.map(end, isPureInsertion ? 1 : -1);
                if (isDeletion ? replacementStart > replacementEnd : replacementStart >= replacementEnd) return;
                const protectedNodePositions = setProtectedGuidNodes(newTr, protectedNodes);
                repairSplitGuidDuplicates(newTr, sourceGuids, protectedNodePositions);
                const startPosition = guidOffsetForPosition(newTr.doc, replacementStart, 'start');
                const endPosition = guidOffsetForPosition(newTr.doc, replacementEnd, 'end');
                this.view.dispatch(newTr);
                return {
                    startGuid: startPosition.guid,
                    startOffset: startPosition.offset,
                    endGuid: endPosition.guid,
                    endOffset: endPosition.offset,
                };
            },
            insertSiblingBlocksAfterGuid: (afterGuid, content) => {
                const anchorEntry = findParagraphGuidNode(this.state.doc, afterGuid);
                if (anchorEntry === undefined) return undefined;
                const documentGuidNodes = collectGuidNodes(this.state.doc);
                documentGuidNodes.forEach(nodes => {
                    if (nodes.length > 1) throw new Error('Document contains duplicate guid.');
                });
                const rawGuids = collectFragmentGuids(Fragment.fromJSON(this.state.schema, content));
                if (uniq(rawGuids).length !== rawGuids.length) throw new Error('Replacement contains duplicate guid.');
                rawGuids.forEach(guid => {
                    if (documentGuidNodes.has(guid)) throw new Error('Replacement guid collides with the document.');
                });
                const fragment = normalizeReplacementFragment(this.state.schema, content, new Set(documentGuidNodes.keys()));
                if (fragment.size === 0) return undefined;
                const insertedGuids: string[] = [];
                let missingHandle = false;
                fragment.forEach(node => {
                    const handle = insertedBlockHandleGuid(node);
                    if (handle === undefined) missingHandle = true;
                    else insertedGuids.push(handle);
                });
                if (missingHandle || insertedGuids.length === 0) return undefined;
                // tr.insert는 anchor 노드의 닫힘 태그 바로 뒤(순수 노드 경계)에 형제로 넣는다 — replace(zero-width,
                // maxOpen)와 달리 anchor 문단의 텍스트 안으로 열어 병합하지 않는다(CQA-727: 병합되면 되돌리기 시
                // 두 문단이 하나로 합쳐진 자리에서 guid 경계가 겹쳐 충돌한다).
                // 표 내부 문단인 경우 셀 내부가 아닌 최상위 표 바로 뒤에 형제로 넣는다.
                // numberingList는 문단 하나만 담는 노드라 셀처럼 리스트 안에 삽입될 수 없다 — 슬라이스 맞춤이
                // 리스트 노드를 닫고 그 뒤 형제 위치에 넣으므로 별도 승격 분기는 필요 없다.
                let insertPos = anchorEntry.pos + anchorEntry.node.nodeSize;
                const $pos = this.state.doc.resolve(anchorEntry.pos + 1);
                for (let d = 1; d <= $pos.depth; d++) {
                    const ancestor = $pos.node(d);
                    if (ancestor.type.name === 'table') {
                        insertPos = $pos.after(d);
                        break;
                    }
                }
                this.view.dispatch(this.view.state.tr.insert(insertPos, fragment));
                return { guids: insertedGuids };
            },
            removeSiblingBlocksByGuids: guids => {
                if (guids.length === 0) return false;
                // guid마다 descendants 재순회하지 않고 문서를 한 번만 수집한다 — 같은 수집 결과로 중복 guid도 같이 가려낸다
                // (insertSiblingBlocksAfterGuid의 documentGuidNodes 중복 가드와 같은 경계).
                const documentGuidNodes = collectGuidNodes(this.state.doc);
                const entries = guids.map(guid => {
                    const nodes = documentGuidNodes.get(guid);
                    if (nodes === undefined || nodes.length === 0) throw new Error(`Editor node ${guid} is not initialized.`);
                    if (nodes.length > 1) throw new Error(`Editor node ${guid} is duplicated.`);
                    // numberingList는 guid를 못 갖는다 — insert가 유일한 자식 문단 guid를 핸들로 돌려줬으므로
                    // 그 핸들의 삭제 owner는 부모 리스트로 승격한다(문단만 지우면 빈 리스트가 남는다, CQA-803).
                    const entry = nodes[0];
                    const $pos = this.state.doc.resolve(entry.pos);
                    if ($pos.depth > 0 && $pos.parent.type.name === 'numberingList') {
                        return { node: $pos.parent, pos: $pos.before($pos.depth) };
                    }
                    return entry;
                });
                // 노드 경계가 정확히 맞닿아 있어야만 통째로 지운다 — 사이에 다른 노드가 끼어들었으면(문서가 그 사이 바뀌었으면)
                // 지어내지 않고 던진다.
                for (let index = 1; index < entries.length; index += 1) {
                    if (entries[index - 1].pos + entries[index - 1].node.nodeSize !== entries[index].pos) throw new Error('Guid nodes are not a contiguous sibling range.');
                }
                const from = entries[0].pos;
                const last = entries[entries.length - 1];
                this.view.dispatch(this.view.state.tr.delete(from, last.pos + last.node.nodeSize));
                return true;
            },
            /**
             * @description 현재 선택된 영역을 replacedContent로 교체한다.
             * @param replacedContent 교체할 JSON 배열 데이터
             */
            replaceContentWithSelection: replacedContent => {
                const { from, to } = this.state.selection;
                // selection으로 선택된 영역의 경우 guid가 앞에서 잘려서 들어가게 되므로 guid를 재생성해야 한다.
                const replacedGuids: string[] = [];
                const fragment = Fragment.fromJSON(this.state.schema, renewGuidInProseMirrorJSON(replacedContent));
                fragment.descendants((node, pos) => {
                    if (node.attrs.guid) {
                        replacedGuids.push(node.attrs.guid);
                    }
                });
                const newSlice = new Slice(fragment, 0, 0);
                const newTr = this.view.state.tr.replace(from, to, newSlice);
                this.view.dispatch(newTr);
                return {
                    ...this.utils.getContentsJsonWithGuids(replacedGuids[0], replacedGuids[replacedGuids.length - 1]),
                    replacedStartGuid: replacedGuids[0],
                    replacedEndGuid: replacedGuids[replacedGuids.length - 1],
                };
            },
            // 라벨 위젯은 번호가 붙는 문단의 contentDOM 안에 들어간다 — 가장 가까운 [data-guid] 조상이 곧 그 문단이다.
            getListNumberLabels: () => {
                const labels = new Map<string, string>();
                this.view.dom.querySelectorAll<HTMLElement>('.list-number').forEach(labelElement => {
                    const guid = labelElement.closest<HTMLElement>('[data-guid]')?.dataset.guid;
                    const text = labelElement.querySelector('span')?.textContent ?? '';
                    if (typeof guid === 'string' && guid.length > 0 && text.length > 0) labels.set(guid, text);
                });
                return labels;
            },
            /**
             * @description 노드 배열에서 절대값이 가장 큰 minus indent 값을 찾아서 반환합니다. 주로 여백이 적거나 없는 PartViewer에서 minus indent가 있는 문서를 대응하기 위해 사용합니다.
             * @param elements // 순회할 노드 배열
             * @returns 절대값이 가장 큰 minus indent 값
             */
            getMinLeftMargin: () => {
                // 절대값이 가장 큰 minus indent 값을 순회하여 찾아낸다.
                let maxMinusLeftMargin = 0;
                const document = this.state.doc;
                document.descendants((node, pos) => {
                    if (node.attrs && (node.attrs.firstLineIndent || node.attrs.indent)) {
                        const firstLineIndent = node.attrs.firstLineIndent ? convertToPt(node.attrs.firstLineIndent) : 0;
                        const indent = node.attrs.indent ? convertToPt(node.attrs.indent) : 0;
                        if (maxMinusLeftMargin > firstLineIndent) {
                            maxMinusLeftMargin = firstLineIndent;
                        }
                        if (maxMinusLeftMargin > indent) {
                            maxMinusLeftMargin = indent;
                        }
                    }
                });
                return maxMinusLeftMargin;
            },
        };
    };
}

/**
 * 현재 선택된 영역에 특정 노드 타입의 노드를 찾아서 반환합니다
 */
export function selectionHas(state: Editor['state'], nodeType: NodeType) {
    const { doc, selection } = state;

    let found = false;
    doc.nodesBetween(selection.from, selection.to, node => {
        if (node.type === nodeType) {
            found = true;
            return false; // 찾았으면 더 이상 찾지 않음
        }
        return true;
    });
    return found;
}

/** pos 위치로부터 상위 노드를 탐색하여 typeName과 일치하는 노드가 있는지 확인합니다. */
export const findParentByNodeTypeName = (pos: ResolvedPos, typeName: string) => {
    let depth = pos.depth;

    while (depth > 0) {
        if (pos.node(depth).type.name === typeName) return true;
        depth--;
    }

    return false;
};
